import { useEffect, useState, type ReactNode } from 'react';
import { Link, useNavigate } from 'react-router';
import { httpsCallable } from 'firebase/functions';
import {
  sessionReminderDays,
  CAPTURE_DEFAULTS,
  CONSENT_VERSIONS,
  DEFAULT_EXERCISE_REMINDER_DAYS,
  DEFAULT_EXERCISE_REMINDER_HOUR,
  DEFAULT_WORK_HOURS,
  exerciseReminderOn,
  NUDGES,
} from '@erectus/shared';
import type { FixedReminder, NudgeKind, UserSettings } from '@erectus/shared';
import { Button, ErrorState, Pill, Toggle } from '@/ui/primitives.js';
import { PageHeader } from '@/ui/Layout.js';
import { CollapsibleCard } from '@/ui/Collapsible.js';
import { useAuth, canOptInToDataSharing } from '@/stores/auth.js';
import { saveBodyDetails, saveHeight, saveSettings } from '@/lib/repo.js';
import { clearFirestoreCache, firebase } from '@/lib/firebase.js';
import { wipeLocalData } from '@/session/db.js';
import { saveAnalyticsConsent, storedAnalyticsConsent, track } from '@/lib/analytics.js';
import { isNative } from '@/lib/device.js';
import { DayPicker, HourPicker, TimePicker, formatHour, formatMinute } from '@/ui/pickers.js';
import { WorkHoursField } from '@/features/program/WorkHoursField.js';
import { SexField, WeightField } from '@/features/onboarding/BodyFields.js';
import { AppleRevokeError, needsAppleRevoke, revokeAppleToken } from '@/lib/appleRevoke.js';
import {
  DOW_LABELS,
  enableReminders,
  nextOccurrence,
  reminderPermission,
  type ReminderPermission,
} from '@/lib/reminders.js';
import { HeightField } from '@/features/check/HeightField.js';
import { ReplayStorage } from './ReplayStorage.js';
import { regionUsesImperial, setUnitPreference } from '@/lib/units.js';

const ALERT_LABEL = { silent: 'Silent', sound: 'Sound', vibrate: 'Vibrate' } as const;
import { LEGAL_DOCS } from '@/features/legal/LegalPage.js';

const DEFAULTS: UserSettings = {
  reminderEveryDays: 7,
  reminderDow: 1,
  reminderHour: 10,
  alertMode: 'silent',
  sampleFps: CAPTURE_DEFAULTS.sampleFps,
  dimByDefault: false,
};

export function SettingsPage(): ReactNode {
  const { user, userDoc, refreshUserDoc, logout } = useAuth();
  const navigate = useNavigate();
  const [settings, setSettings] = useState<UserSettings>(userDoc?.settings ?? DEFAULTS);
  const [permission, setPermission] = useState<ReminderPermission>('default');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmText, setConfirmText] = useState('');
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (userDoc?.settings) setSettings(userDoc.settings);
  }, [userDoc?.settings]);

  useEffect(() => {
    void reminderPermission().then(setPermission);
  }, []);

  const persist = async (patch: Partial<UserSettings>): Promise<void> => {
    if (!user) return;
    const next = { ...settings, ...patch };
    setSettings(next);
    setSaving(true);
    setError(null);
    try {
      // The server reads the schedule from here; the device holds no timer.
      await saveSettings(user.uid, next);
      if (patch.reminderEveryDays) track('reminder_set', { every_days: patch.reminderEveryDays });
      await refreshUserDoc();
    } catch {
      setError('Could not save. Check your connection.');
    } finally {
      setSaving(false);
    }
  };

  const askPermission = async (): Promise<void> => {
    if (!user) return;
    setPermission(await enableReminders(user.uid));
  };

  const deleteAccount = async (): Promise<void> => {
    if (!user || confirmText !== 'DELETE') return;
    setDeleting(true);
    setError(null);
    // Apple-linked accounts: revoke the Apple token first (App Store 5.1.1(v)).
    // A cancelled or failed confirmation stops here, before anything is deleted.
    if (needsAppleRevoke()) {
      try {
        await revokeAppleToken();
      } catch (revokeError) {
        setError(revokeError instanceof AppleRevokeError ? revokeError.message : 'Could not disconnect your Apple account.');
        setDeleting(false);
        return;
      }
    }
    try {
      const callable = httpsCallable<Record<string, never>, { deleted: boolean }>(
        firebase().functions,
        'deleteUserData',
      );
      await callable({});
      // Both on-device copies: our own session buffer and Firestore's cache.
      await wipeLocalData();
      await clearFirestoreCache();
      track('account_deleted', { confirmed: true });
      await logout();
      navigate('/login', { replace: true });
    } catch {
      setError('Deletion failed. Please try again, or email us so we can do it manually.');
      setDeleting(false);
    }
  };

  const optInEligible = canOptInToDataSharing(userDoc?.profile?.birthYear);
  const [usageStats, setUsageStats] = useState(storedAnalyticsConsent);

  const exerciseOn = exerciseReminderOn(settings, (userDoc?.gamification?.lastSessionAt ?? 0) > 0);

  const reminderSummary = [
    ...(permission === 'denied' ? ['Notifications blocked'] : []),
    settings.sessionReminder === false
      ? 'Session: off'
      : `Session: ${describeDays(sessionReminderDays(settings))} at ${formatHour(settings.reminderHour)}`,
    exerciseOn
      ? `Exercise: ${describeDays(settings.exerciseReminderDays ?? [...DEFAULT_EXERCISE_REMINDER_DAYS])} at ${formatHour(settings.exerciseReminderHour ?? DEFAULT_EXERCISE_REMINDER_HOUR)}`
      : 'Exercise: off',
  ].join(' · ');
  // Two choices; until one is picked, the US gets imperial and everyone else metric.
  const shownUnits = settings.units === 'imperial' || settings.units === 'metric' ? settings.units : regionUsesImperial() ? 'imperial' : 'metric';
  const sessionSummary = `Alerts: ${ALERT_LABEL[settings.alertMode].toLowerCase()}`;
  const work = settings.workHours;
  const dailyOn = settings.breakNudges !== false && settings.breakEveryMin !== 0;
  const workdaySummary = work
    ? `${describeDays(work.days)} ${formatMinute(work.start)}–${formatMinute(work.end)} · daily reminder ${dailyOn ? 'on' : 'off'}`
    : 'Set your work hours to get a daily move reminder';

  return (
    <div className="space-y-4">
      <PageHeader title="Settings" subtitle={user?.email ?? undefined} />

      {error ? <ErrorState title="Something went wrong" message={error} /> : null}

      <CollapsibleCard id="reminders" title="Reminders" summary={reminderSummary} className="space-y-4">
        {permission !== 'granted' ? (
          <div className="rounded-xl bg-surface p-3 text-sm">
            {permission === 'unsupported' ? (
              <p className="text-muted">
                {isNative()
                  ? 'Reminders could not be set up on this device. Your schedule is still saved.'
                  : 'This browser cannot show reminders. The mobile app can.'}
              </p>
            ) : permission === 'denied' ? (
              <p className="text-muted">
                Notifications are blocked in your {isNative() ? 'phone' : 'browser'} settings. Your
                schedule is still saved.
              </p>
            ) : (
              <Button variant="secondary" onClick={() => void askPermission()}>
                Allow reminders
              </Button>
            )}
          </div>
        ) : null}

        <ReminderBlock
          title="Session reminder"
          description="A nudge to record today's session. Skipped on a day you already have."
          on={settings.sessionReminder !== false}
          onToggle={(value) => {
            void persist({ sessionReminder: value });
            if (value && permission === 'default') void askPermission();
          }}
          days={sessionReminderDays(settings)}
          onDays={(days) => void persist({ reminderDays: days })}
          hour={settings.reminderHour}
          onHour={(hour) => void persist({ reminderHour: hour })}
        />

        <div className="border-t border-line" />

        <ReminderBlock
          title="Exercise reminder"
          description="Darwin's picks for the week — the same ones shown on Progress."
          on={exerciseOn}
          onToggle={(value) => {
            void persist({ exerciseReminder: value });
            if (value && permission === 'default') void askPermission();
          }}
          days={settings.exerciseReminderDays ?? [...DEFAULT_EXERCISE_REMINDER_DAYS]}
          onDays={(days) => void persist({ exerciseReminderDays: days })}
          hour={settings.exerciseReminderHour ?? DEFAULT_EXERCISE_REMINDER_HOUR}
          onHour={(hour) => void persist({ exerciseReminderHour: hour })}
        />
      </CollapsibleCard>

      <CollapsibleCard id="workday" title="Workday reminders" summary={workdaySummary} className="space-y-4">
        <p className="text-sm text-ink-soft">
          Sessions only measure. Breaks and exercise snacks happen in your day. Desk-work guidance suggests
          getting up every 30–50 minutes for a few minutes —{' '}
          <Link className="underline" to="/sources#breaks">
            sources
          </Link>
          .
        </p>
        {work ? (
          <WorkHoursField value={work} onChange={(workHours) => void persist({ workHours })} />
        ) : (
          <Button variant="secondary" onClick={() => void persist({ workHours: DEFAULT_WORK_HOURS })}>
            Set work hours (weekdays 9–6)
          </Button>
        )}
        {work ? (
          <>
            <div className="border-t border-line" />
            <Toggle
              checked={dailyOn}
              onChange={(value) => {
                void persist({
                  breakNudges: value,
                  snackNudges: value,
                  ...(value && settings.breakEveryMin === 0 ? { breakEveryMin: NUDGES.breakEveryMinDefault } : {}),
                });
                if (value && permission === 'default') void askPermission();
              }}
              label="Daily move reminder"
              description="Once a workday, at a different time each day: get up for a few minutes. On an exercise day it brings today's snack."
            />
          </>
        ) : null}
        <div className="border-t border-line" />
        <FixedReminders
          reminders={settings.fixedReminders ?? []}
          onChange={(fixedReminders) => void persist({ fixedReminders })}
        />
      </CollapsibleCard>

      <CollapsibleCard id="session" title="During a session" summary={sessionSummary} className="space-y-4">
        <div>
          <p className="mb-1 text-sm font-medium">Units shown</p>
          <div className="flex flex-wrap gap-2">
            {(['imperial', 'metric'] as const).map((u) => (
              <Pill
                key={u}
                active={shownUnits === u}
                onClick={() => {
                  setUnitPreference(u);
                  void persist({ units: u });
                }}
              >
                {u === 'imperial' ? 'Imperial' : 'Metric'}
              </Pill>
            ))}
          </div>
          <p className="mt-1 text-xs text-muted">Only what you see. Measurements are always kept in centimetres and degrees.</p>
        </div>
        {user ? (
          <HeightField
            key={shownUnits}
            heightCm={userDoc?.profile?.heightCm ?? null}
            onSave={(cm) => void saveHeight(user.uid, cm).then(refreshUserDoc).catch(() => undefined)}
          />
        ) : null}
        {user ? (
          <WeightField
            key={`w-${shownUnits}`}
            weightKg={userDoc?.profile?.weightKg ?? null}
            onSave={(kg) => void saveBodyDetails(user.uid, { weightKg: kg }).then(refreshUserDoc).catch(() => undefined)}
          />
        ) : null}
        {user ? (
          <SexField
            value={userDoc?.profile?.sex ?? null}
            onChange={(sex) => void saveBodyDetails(user.uid, { sex }).then(refreshUserDoc).catch(() => undefined)}
          />
        ) : null}
        <div>
          <p className="mb-2 text-sm font-medium">Alerts</p>
          <div className="flex gap-2">
            {(['silent', 'sound', 'vibrate'] as const).map((mode) => (
              <Pill
                key={mode}
                active={settings.alertMode === mode}
                onClick={() => void persist({ alertMode: mode })}
              >
                {ALERT_LABEL[mode]}
              </Pill>
            ))}
          </div>
        </div>
        <Toggle
          checked={settings.dimByDefault}
          onChange={(value) => void persist({ dimByDefault: value })}
          label="Start sessions with the screen dimmed"
          description="Low on battery? Turn this on — the screen uses more power than the measuring."
        />
      </CollapsibleCard>

      <CollapsibleCard id="data" title="Your data" summary={`Detection-improvement sharing: ${userDoc?.consent?.dataOptIn ? 'on' : 'off'}`} className="space-y-3">
        <p className="text-sm text-ink-soft">
          Session summaries are stored in your account. Video is never uploaded — it is processed on
          this device and discarded.
        </p>
        <ReplayStorage />
        <Toggle
          checked={usageStats}
          onChange={(value) => {
            setUsageStats(value);
            saveAnalyticsConsent(value);
          }}
          label="Usage statistics"
          description="Which screens and features get used, so we can improve the app. Never your posture numbers."
        />
        <p className="text-sm">
          Detection-improvement sharing:{' '}
          <strong>{userDoc?.consent?.dataOptIn ? 'On' : 'Off'}</strong>
        </p>
        {optInEligible ? (
          <Link to="/settings/data" className="text-sm font-semibold underline">
            Manage sharing
          </Link>
        ) : (
          <p className="text-xs text-muted">Sharing is available from age 18.</p>
        )}
        <p className="text-xs text-muted">
          Consent recorded {userDoc?.consent ? new Date(userDoc.consent.acceptedAt).toLocaleDateString() : '—'} ·
          terms v{CONSENT_VERSIONS.tos}
        </p>
      </CollapsibleCard>

      <CollapsibleCard id="policies" title="Policies" summary="Privacy, health data, terms, sources" className="space-y-2">
        <ul className="grid gap-1 text-sm">
          <li>
            <Link className="underline" to="/sources">
              Where the numbers come from
            </Link>
          </li>
          {LEGAL_DOCS.map((doc) => (
            <li key={doc.slug}>
              <Link className="underline" to={`/legal/${doc.slug}`}>
                {doc.title}
              </Link>
            </li>
          ))}
        </ul>
      </CollapsibleCard>

      <CollapsibleCard id="account" title="Account" summary={user?.email ?? 'Sign out, delete account'} className="space-y-3">
        <Button variant="secondary" onClick={() => void logout()}>
          Sign out
        </Button>
        <div className="rounded-xl border border-[color:var(--color-state-slouch)] p-3">
          <p className="text-sm font-semibold">Delete account and all data</p>
          <p className="mt-1 text-xs text-muted">
            Removes every session, summary, uploaded keypoint file and your sign-in. This cannot be
            undone. Type DELETE to confirm.
          </p>
          <input
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            placeholder="DELETE"
            aria-label="Type DELETE to confirm"
            className="mt-2 w-full rounded-lg border border-line px-3 py-2 text-sm"
          />
          <Button
            variant="danger"
            className="mt-2"
            disabled={confirmText !== 'DELETE' || deleting}
            onClick={() => void deleteAccount()}
          >
            {deleting ? 'Deleting…' : 'Delete everything'}
          </Button>
        </div>
      </CollapsibleCard>

      {saving ? <p className="text-xs text-muted">Saving…</p> : null}
    </div>
  );
}

/** "Every day", "Weekdays", "Weekends", or "Mon, Wed, Fri". */
export function describeDays(days: readonly number[]): string {
  const set = [...new Set(days)].sort((a, b) => a - b).join(',');
  if (set === '0,1,2,3,4,5,6') return 'Every day';
  if (set === '1,2,3,4,5') return 'Weekdays';
  if (set === '0,6') return 'Weekends';
  return set
    .split(',')
    .map((d) => DOW_LABELS[Number(d)]!.slice(0, 3))
    .join(', ');
}

/** One reminder: an on/off switch, then its days and time when on. */
function ReminderBlock({
  title,
  description,
  on,
  onToggle,
  days,
  onDays,
  hour,
  onHour,
}: {
  title: string;
  description: string;
  on: boolean;
  onToggle(value: boolean): void;
  days: number[];
  onDays(days: number[]): void;
  hour: number;
  onHour(hour: number): void;
}): ReactNode {
  const next = on ? nextOccurrence(days, hour) : null;
  return (
    <div className="space-y-3">
      <Toggle checked={on} onChange={onToggle} label={title} description={description} />
      {on ? (
        <div className="space-y-3 pl-8">
          <DayPicker days={days} onChange={onDays} label={`${title} days`} />
          <HourPicker hour={hour} onChange={onHour} label="Time" />
          <p className="text-xs text-muted">
            {describeDays(days)} at {formatHour(hour)}
            {next
              ? ` · next ${next.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' })}`
              : ''}
          </p>
        </div>
      ) : null}
    </div>
  );
}

const KIND_LABEL: Record<NudgeKind, string> = { break: 'Rest break', snack: 'Exercise snack', check: 'Camera check' };

/** Reminders at a set time, on top of the varied ones. Up to five. */
function FixedReminders({
  reminders,
  onChange,
}: {
  reminders: FixedReminder[];
  onChange(next: FixedReminder[]): void;
}): ReactNode {
  const update = (id: string, patch: Partial<FixedReminder>): void =>
    onChange(reminders.map((r) => (r.id === id ? { ...r, ...patch } : r)));
  return (
    <div className="space-y-3">
      <div>
        <p className="text-sm font-medium">At a set time</p>
        <p className="text-xs text-muted">For a break or snack you want at the same time every day, like after lunch.</p>
      </div>
      {reminders.map((r) => (
        <div key={r.id} className="space-y-2 rounded-xl border border-line p-3">
          <div className="flex flex-wrap gap-2">
            {(Object.keys(KIND_LABEL) as NudgeKind[]).map((kind) => (
              <Pill key={kind} active={r.kind === kind} onClick={() => update(r.id, { kind })}>
                {KIND_LABEL[kind]}
              </Pill>
            ))}
          </div>
          <DayPicker days={r.days} onChange={(days) => update(r.id, { days })} label="Days" />
          <TimePicker
            label="Time"
            minute={r.minute}
            step={NUDGES.tickMin}
            onChange={(minute) => update(r.id, { minute })}
          />
          <button className="text-xs text-muted underline" onClick={() => onChange(reminders.filter((x) => x.id !== r.id))}>
            Remove
          </button>
        </div>
      ))}
      {reminders.length < 5 ? (
        <Button
          variant="secondary"
          onClick={() =>
            onChange([
              ...reminders,
              { id: `r${Date.now().toString(36)}`, kind: 'snack', minute: 13 * 60 + 30, days: [1, 2, 3, 4, 5] },
            ])
          }
        >
          Add a reminder
        </Button>
      ) : null}
    </div>
  );
}
